import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, CircularProgress, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Warehouse as WarehouseIcon,
  Warning as WarningIcon, Place as PlaceIcon,
} from '@mui/icons-material';
import { Autocomplete as MapsAutocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import { useGoogleMapsLoader } from './GoogleMapsLoader';
import { catalogErrorText } from './bookCatalogErrors';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const EMPTY_FORM = {
  name: '', streetLine1: '', city: '', state: '', zipCode: '',
  country: 'US', latitude: '', longitude: '', isActive: true,
};

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '350px', borderRadius: '8px' };

// For the memoized loaders and map callbacks: the hook's `t` would make them change with the
// language and refetch the warehouses on every switch.
const tWarehouses = (key, options) => i18n.t(key, { ns: 'warehouses', ...options });

function parseAddressComponents(components) {
  const get = (type, useShort = false) => {
    const comp = components.find((c) => c.types.includes(type));
    return comp ? (useShort ? comp.short_name : comp.long_name) : '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const streetLine1 = [streetNumber, route].filter(Boolean).join(' ');

  return {
    streetLine1,
    city: get('locality') || get('sublocality_level_1') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1', true),
    zipCode: get('postal_code'),
    country: 'US',
  };
}

const WarehouseManagementPage = () => {
  const { t } = useTranslation('warehouses');
  const { getAccessToken } = useAuth();
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [markerPos, setMarkerPos] = useState(null);
  const autocompleteRef = useRef(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tWarehouses('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const fetchWarehouses = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/warehouses`, { headers });
      if (!res.ok) throw new Error(tWarehouses('errors.fetchFailed'));
      const data = await res.json();
      setWarehouses(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      toast.error(catalogErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);

  const openCreate = () => {
    setIsEdit(false);
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setMarkerPos(null);
    setDialogOpen(true);
  };

  const openEdit = (w) => {
    setIsEdit(true);
    setEditId(w.id);
    setForm({
      name: w.name || '',
      streetLine1: w.streetLine1 || '',
      city: w.city || '',
      state: w.state || '',
      zipCode: w.zipCode || '',
      country: w.country || 'US',
      latitude: w.latitude ?? '',
      longitude: w.longitude ?? '',
      isActive: w.isActive ?? w.active ?? true,
    });
    if (w.latitude && w.longitude) {
      setMarkerPos({ lat: parseFloat(w.latitude), lng: parseFloat(w.longitude) });
    } else {
      setMarkerPos(null);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const missing = [];
    if (!form.name) missing.push(t('requiredFields.name'));
    if (!form.city) missing.push(t('requiredFields.city'));
    if (!form.state) missing.push(t('requiredFields.state'));
    if (!form.zipCode) missing.push(t('requiredFields.zipCode'));
    if (missing.length > 0) {
      toast.error(t('errors.missingFields', { fields: missing.join(', ') }));
      return;
    }
    try {
      const headers = await authHeaders();
      const body = {
        ...form,
        latitude: form.latitude !== '' ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
      };
      const url = isEdit
        ? `${config.API_BASE_URL}/api/v1/content/admin/warehouses/${editId}`
        : `${config.API_BASE_URL}/api/v1/content/admin/warehouses`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          translateServerMessage(err.message, t(isEdit ? 'errors.updateFailed' : 'errors.createFailed'))
        );
      }
      toast.success(t(isEdit ? 'toast.updated' : 'toast.created'));
      setDialogOpen(false);
      fetchWarehouses();
    } catch (err) {
      toast.error(catalogErrorText(err));
    }
  };

  const handleToggleActive = async (w) => {
    const currentActive = w.isActive ?? w.active ?? false;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/warehouses/${w.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: w.name,
          streetLine1: w.streetLine1,
          city: w.city,
          state: w.state,
          zipCode: w.zipCode,
          country: w.country,
          latitude: w.latitude,
          longitude: w.longitude,
          isActive: !currentActive,
        }),
      });
      if (!res.ok) throw new Error(t('errors.statusFailed'));
      toast.success(t(!currentActive ? 'toast.activated' : 'toast.deactivated'));
      fetchWarehouses();
    } catch (err) {
      toast.error(catalogErrorText(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.item) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${config.API_BASE_URL}/api/v1/content/admin/warehouses/${deleteConfirm.item.id}`,
        { method: 'DELETE', headers },
      );
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.deleted'));
      setDeleteConfirm({ open: false, item: null });
      fetchWarehouses();
    } catch (err) {
      toast.error(catalogErrorText(err));
      setDeleteConfirm({ open: false, item: null });
    }
  };

  const updateField = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const onAutocompleteLoad = useCallback((ac) => {
    autocompleteRef.current = ac;
  }, []);

  const onPlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    if (!place || !place.address_components) return;

    const parsed = parseAddressComponents(place.address_components);
    const loc = place.geometry?.location;
    const lat = loc ? loc.lat() : '';
    const lng = loc ? loc.lng() : '';

    setForm((prev) => ({
      ...prev,
      streetLine1: parsed.streetLine1 || prev.streetLine1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zipCode: parsed.zipCode || prev.zipCode,
      country: 'US',
      latitude: lat,
      longitude: lng,
    }));

    if (loc) {
      setMarkerPos({ lat: loc.lat(), lng: loc.lng() });
    }
  }, []);

  const reverseGeocode = useCallback((latLng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const parsed = parseAddressComponents(results[0].address_components);
        setForm((prev) => ({
          ...prev,
          streetLine1: parsed.streetLine1 || prev.streetLine1,
          city: parsed.city || prev.city,
          state: parsed.state || prev.state,
          zipCode: parsed.zipCode || prev.zipCode,
          country: 'US',
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
      }
    });
  }, []);

  const onMarkerDragEnd = useCallback((e) => {
    const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setMarkerPos(latLng);
    setForm((prev) => ({ ...prev, latitude: latLng.lat, longitude: latLng.lng }));
    reverseGeocode(latLng);
  }, [reverseGeocode]);

  // Allow typing latitude/longitude manually; move the pin once both are valid numbers.
  const onLatLngChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    const lat = parseFloat(field === 'latitude' ? value : form.latitude);
    const lng = parseFloat(field === 'longitude' ? value : form.longitude);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      setMarkerPos({ lat, lng });
    }
  };

  // Geocode the typed address (for when the address wasn't picked from the search box)
  // so the map + latitude/longitude populate once the address fields are filled in.
  const geocodeFromAddress = useCallback(() => {
    if (!window.google) return;
    const address = [form.streetLine1, form.city, form.state, form.zipCode]
      .filter(Boolean)
      .join(', ');
    if (!address.trim()) {
      toast.info(tWarehouses('toast.enterAddressFirst'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
        setMarkerPos({ lat, lng });
      } else {
        toast.error(tWarehouses('errors.addressNotFound'));
      }
    });
  }, [form.streetLine1, form.city, form.state, form.zipCode]);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('header.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('header.subtitle')}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('header.refresh')}>
            <IconButton onClick={fetchWarehouses} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {t('header.add')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {!loading && (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.name')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.city')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.state')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.zip')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('table.active')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <WarehouseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">{t('table.empty')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                warehouses.map((w) => (
                  <TableRow key={w.id} hover>
                    <TableCell>{w.name}</TableCell>
                    <TableCell>{w.city}</TableCell>
                    <TableCell>{w.state}</TableCell>
                    <TableCell>{w.zipCode}</TableCell>
                    <TableCell>
                      <Switch
                        checked={w.isActive ?? w.active ?? false}
                        onChange={() => handleToggleActive(w)}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('table.edit')}>
                        <IconButton size="small" onClick={() => openEdit(w)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('table.delete')}>
                        <IconButton size="small" color="error"
                          onClick={() => setDeleteConfirm({ open: true, item: w })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? t('dialog.editTitle') : t('dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box mt={1} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mapsLoaded && (
              <>
                <MapsAutocomplete
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={onPlaceChanged}
                  options={{ componentRestrictions: { country: 'us' }, fields: ['address_components', 'geometry'] }}
                >
                  <TextField
                    fullWidth
                    placeholder={t('dialog.addressSearch')}
                    size="small"
                    variant="outlined"
                  />
                </MapsAutocomplete>
                <Box>
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={markerPos || DEFAULT_CENTER}
                    zoom={markerPos ? 16 : 4}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {markerPos && (
                      <Marker
                        position={markerPos}
                        draggable
                        onDragEnd={onMarkerDragEnd}
                      />
                    )}
                  </GoogleMap>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('dialog.mapHelp')}
                  </Typography>
                </Box>
              </>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('dialog.name')} value={form.name} onChange={updateField('name')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('dialog.street')} value={form.streetLine1} onChange={updateField('streetLine1')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('dialog.city')} value={form.city} onChange={updateField('city')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('dialog.state')}</InputLabel>
                  <Select value={form.state} onChange={updateField('state')} label={t('dialog.state')}>
                    {US_STATES.map((s) => (
                      <MenuItem key={s.code} value={s.code}>
                        {t(`states.${s.code}`, { defaultValue: s.name })} ({s.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('dialog.zipCode')} value={form.zipCode} onChange={updateField('zipCode')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('dialog.country')} value={form.country} onChange={updateField('country')} />
              </Grid>
              {mapsLoaded && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlaceIcon />}
                    onClick={geocodeFromAddress}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('dialog.findOnMap')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('dialog.latitude')} type="number" value={form.latitude} onChange={onLatLngChange('latitude')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('dialog.longitude')} type="number" value={form.longitude} onChange={onLatLngChange('longitude')} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} color="success" />}
                  label={form.isActive ? t('dialog.active') : t('dialog.inactive')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>{t('dialog.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {isEdit ? t('dialog.update') : t('dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, item: null })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" /> {t('deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            <Trans
              t={t}
              i18nKey="deleteDialog.body"
              values={{ name: deleteConfirm.item?.name ?? '' }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, item: null })} sx={{ textTransform: 'none' }}>{t('deleteDialog.cancel')}</Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600 }}>{t('deleteDialog.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehouseManagementPage;
